import React, { useState, useEffect, useCallback } from 'react'
import { Select, Modal, Input, Button } from 'antd'
import { connect } from 'react-redux'
import { Key } from 'rc-select/lib/interface/generator'
import TimeInput from 'Containers/DimensionFilters/DimensionGroups/Customer/Customer.SegmentScreen.TimeInput'
import { TREE_DIMENSIONS } from 'Containers/DimensionFilters/constants'
import TreeSelect from 'Containers/DimensionFilters/Inputs/CommonInputs/TreeSelect'
import useFetchDimensions from 'Containers/DimensionFilters/DimensionGroups/Customer/useFetchDimensions'
import ProductSelectInput from './ProductSelectInput'
import CategoryTreeInput from './CategoryTreeInput'
import PromotionSelectInput from './PromotionSelectInput'
import VoucherSelectInput from './VoucherSelectInput'

const { Option } = Select

interface Product {
  name?: string
  code?: string
  image?: string | null
  label?: string
  value?: string
  id?: string
  sku?: string
}

interface DimensionValue {
  code: string
  name: string
}

interface DimensionOperator {
  code: string
  name: string
  values?: DimensionValue[]
}

interface Dimension {
  code: string
  name: string
  operator?: DimensionOperator[]
  timeOperator?: DimensionOperator[]
  values?: DimensionValue[]
  extraField?: Array<{ code: string }>
}

interface Props {
  visible: boolean
  toggleDimensionModal: (visible: boolean) => void
  addSegment: (segment: { audience: AttributeType }) => void
  updateSegment: (params: { groupIndex: number; index: number; segment: AttributeType }) => void
  delSegment: (params: { groupIndex: number; index: number }) => void
  dimensions: Dimension[]
  onDimensionModalCancel: () => void
  editingSegment?: { index: number; data: AttributeType }
  currentGroup: number
}

interface AttributeType {
  type?: string
  operator?: string
  value?: string
  timeOperator?: string
  time_value?: string
  count?: string
}

const UserAttributeModal2: React.FC<Props> = ({
  visible,
  toggleDimensionModal,
  onDimensionModalCancel,
  addSegment,
  updateSegment,
  delSegment,
  dimensions,
  editingSegment,
  currentGroup,
}) => {
  const [attributes, setAttributes] = useState<AttributeType>({
    type: undefined,
    operator: undefined,
    value: undefined,
    timeOperator: undefined,
    time_value: undefined,
    count: undefined,
  })
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedPromotions, setSelectedPromotions] = useState<string[]>([])
  const [selectedVouchers, setSelectedVouchers] = useState<string[]>([])
  const [selectedPromotionsForVoucher, setSelectedPromotionsForVoucher] = useState<string[]>([])

  const selectedType =
    attributes && dimensions.find((item: { code: any }) => item.code === attributes.type)
  useEffect(() => {
    if (visible) {
      if (editingSegment && editingSegment.data) {
        // Pre-fill form with existing data for editing
        setAttributes(editingSegment.data)
        
        // If there's an existing value for ORDER_HAS_PRODUCT, parse it
        if (editingSegment.data?.type === 'ORDER_HAS_PRODUCT' && editingSegment.data?.value) {
          // Parse product codes if needed
          // The value might be stored as comma-separated product codes
          const productCodes = editingSegment.data.value.split(',').filter(code => code.trim())
          // Create placeholder product objects from the codes
          // These will be updated with actual data after API search
          const products = productCodes.map(code => ({
            code: code.trim(),
            value: code.trim(),
            name: code.trim(), // Will be replaced with actual name when loaded
            label: code.trim()
          }))
          setSelectedProducts(products)
        }
        
        // If there's an existing value for ORDER_HAS_PRODUCT_IN_CATEGORY, parse it
        if (editingSegment.data?.type === 'ORDER_HAS_PRODUCT_IN_CATEGORY' && editingSegment.data?.value) {
          // Parse category codes - handle commas in category names properly
          let categoryCodes: string[] = []

          // Check if it's JSON format (new format)
          if (editingSegment.data.value.startsWith('[')) {
            try {
              categoryCodes = JSON.parse(editingSegment.data.value)
            } catch {
              console.error('Failed to parse category codes as JSON')
            }
          } else {
            // Legacy format: Categories are in format "CATEGORY_<id>_<name>" where name can contain commas
            // Split by ",CATEGORY_" to handle commas within category names
            const parts = editingSegment.data.value.split(',CATEGORY_')

            categoryCodes = parts.map((part, index) => {
              // Add back the CATEGORY_ prefix for all parts except the first one
              if (index === 0) {
                return part.trim()
              } else {
                return 'CATEGORY_' + part.trim()
              }
            }).filter(code => code) // Remove empty strings
          }

          console.log('Parsed category codes:', categoryCodes)
          setSelectedCategories(categoryCodes)
        }
        
        // If there's an existing value for ORDER_HAS_PROMOTION, parse it
        if (editingSegment.data?.type === 'ORDER_HAS_PROMOTION' && editingSegment.data?.value) {
          const promotionIds = editingSegment.data.value.split(',')
          setSelectedPromotions(promotionIds)
        }
        
        // If there's an existing value for ORDER_HAS_VOUCHER, parse it
        if (editingSegment.data?.type === 'ORDER_HAS_VOUCHER' && editingSegment.data?.value) {
          const voucherIds = editingSegment.data.value.split(',')
          setSelectedVouchers(voucherIds)
        }

        // If there's an existing value for VOUCHER_USABLE_BY_PROMOTION, parse it
        if (editingSegment.data?.type === 'VOUCHER_USABLE_BY_PROMOTION' && editingSegment.data?.value) {
          const promotionIds = editingSegment.data.value.split(',')
          setSelectedPromotionsForVoucher(promotionIds)
        }
      } else {
        // Reset form for new segment
        setAttributes({
          type: undefined,
          operator: undefined,
          value: undefined,
          timeOperator: undefined,
          time_value: undefined,
          count: undefined,
        })
        setSelectedProducts([])
        setSelectedCategories([])
        setSelectedPromotions([])
        setSelectedVouchers([])
        setSelectedPromotionsForVoucher([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingSegment])

  const { options, setKeyword, loading, hasMore, onLoadMore } = useFetchDimensions({
    dimension_code: selectedType?.code,
  })

  // Separate hook instance for background product loading
  const {
    options: backgroundOptions,
    setKeyword: setBackgroundKeyword
  } = useFetchDimensions({
    dimension_code: 'ORDER_HAS_PRODUCT',
  })

  // Track background search state
  const [backgroundSearchIndex, setBackgroundSearchIndex] = useState(0)
  const [productCodesToLoad, setProductCodesToLoad] = useState<string[]>([])
  const [isLoadingProductDetails, setIsLoadingProductDetails] = useState(false)

  // Initialize background loading when editing
  useEffect(() => {
    if (editingSegment?.data?.type === 'ORDER_HAS_PRODUCT' &&
        editingSegment?.data?.value &&
        visible) {
      const codes = editingSegment.data.value.split(',').filter(code => code.trim())
      setProductCodesToLoad(codes)
      setBackgroundSearchIndex(0)
      setIsLoadingProductDetails(true)
    }
  }, [editingSegment, visible])

  // Load products sequentially
  useEffect(() => {
    const loadNextProduct = async () => {
      if (productCodesToLoad.length > 0 && backgroundSearchIndex < productCodesToLoad.length && isLoadingProductDetails) {
        const currentCode = productCodesToLoad[backgroundSearchIndex].trim()

        // Trigger search
        setBackgroundKeyword(currentCode)

        // Wait for search results
        await new Promise(resolve => setTimeout(resolve, 800))

        // After delay, move to next product
        setBackgroundSearchIndex(prev => prev + 1)
      } else if (backgroundSearchIndex >= productCodesToLoad.length && productCodesToLoad.length > 0) {
        // Finished loading all products
        console.log('Finished loading all products')
        setIsLoadingProductDetails(false)
        setBackgroundKeyword('')
        setProductCodesToLoad([])
        setBackgroundSearchIndex(0)
      }
    }

    loadNextProduct()
  }, [backgroundSearchIndex, productCodesToLoad, setBackgroundKeyword, isLoadingProductDetails])

  // Update selected products when background options change
  useEffect(() => {
    if (backgroundOptions.length > 0 && productCodesToLoad.length > 0 && backgroundSearchIndex > 0) {
      const currentIndex = backgroundSearchIndex - 1 // We've already incremented, so use previous index
      if (currentIndex >= 0 && currentIndex < productCodesToLoad.length) {
        const currentCode = productCodesToLoad[currentIndex]?.trim()

        if (currentCode) {
          // Find product in background options
          const foundProduct = backgroundOptions.find((opt: Product) => {
            // Check multiple fields for matching
            return opt.code === currentCode ||
                   opt.value === currentCode ||
                   opt.id === currentCode ||
                   opt.sku === currentCode
          })

          if (foundProduct) {
            console.log('Found product for code:', currentCode, foundProduct)

            setSelectedProducts(prev => {
              // Check if this product already exists in the list
              const existingIndex = prev.findIndex(p =>
                p.code === currentCode ||
                p.value === currentCode ||
                p.id === currentCode
              )

              if (existingIndex >= 0) {
                // Update existing product
                const updated = [...prev]
                updated[existingIndex] = {
                  ...foundProduct,
                  // Ensure we keep the code/value that was originally saved
                  code: foundProduct.code || currentCode,
                  value: foundProduct.value || currentCode
                }
                console.log('Updated product at index', existingIndex, ':', updated[existingIndex])
                return updated
              } else {
                // This shouldn't happen if selectedProducts is properly initialized
                console.log('Product not found in selectedProducts, adding it')
                return [...prev, {
                  ...foundProduct,
                  code: foundProduct.code || currentCode,
                  value: foundProduct.value || currentCode
                }]
              }
            })
          } else {
            console.log('Product not found for code:', currentCode, 'in options:', backgroundOptions)
          }
        }
      }
    }
  }, [backgroundOptions, backgroundSearchIndex, productCodesToLoad])

  const renderOptions = (optionType: keyof Dimension) => {
    const options = selectedType?.[optionType] as DimensionOperator[] | undefined
    return (
      options?.map((item) => (
        <Option key={item.code} value={item.code}>
          {item.name}
        </Option>
      )) || []
    )
  }

  const handleProductSelect = (products: Product[]) => {
    setSelectedProducts(products)
    const productCodes = products.map(p => p.code || p.value).join(',')
    setAttributes({ ...attributes, value: productCodes })
  }

  const handleCategorySelect = (categories: string[]) => {
    setSelectedCategories(categories)
    // Use JSON encoding to properly handle categories with commas in their names
    const categoryCodes = JSON.stringify(categories)
    setAttributes({ ...attributes, value: categoryCodes })
  }

  const handlePromotionSelect = (promotions: string[]) => {
    setSelectedPromotions(promotions)
    const promotionIds = promotions.join(',')
    setAttributes({ ...attributes, value: promotionIds })
  }

  const handleVoucherSelect = (vouchers: string[]) => {
    setSelectedVouchers(vouchers)
    const voucherIds = vouchers.join(',')
    setAttributes({ ...attributes, value: voucherIds })
  }

  const handlePromotionForVoucherSelect = (promotions: string[]) => {
    setSelectedPromotionsForVoucher(promotions)
    const promotionIds = promotions.join(',')
    setAttributes({ ...attributes, value: promotionIds })
  }

  const renderOptionsInput = () => {
    if (attributes?.type === 'ORDER_HAS_PRODUCT') {
      return (
        <ProductSelectInput
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          products={options}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          onSearch={setKeyword}
          operator={attributes.operator}
          disabled={!attributes.operator}
        />
      )
    }

    if (attributes?.type === 'ORDER_HAS_PRODUCT_IN_CATEGORY') {
      return (
        <CategoryTreeInput
          selectedCategories={selectedCategories}
          onCategorySelect={handleCategorySelect}
          categories={options}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          onSearch={setKeyword}
          operator={attributes.operator}
          disabled={!attributes.operator}
        />
      )
    }

    if (attributes?.type === 'ORDER_HAS_PROMOTION') {
      return (
        <PromotionSelectInput
          selectedPromotions={selectedPromotions}
          onPromotionSelect={handlePromotionSelect}
          operator={attributes.operator}
          disabled={!attributes.operator}
        />
      )
    }

    if (attributes?.type === 'ORDER_HAS_VOUCHER') {
      return (
        <VoucherSelectInput
          selectedVouchers={selectedVouchers}
          onVoucherSelect={handleVoucherSelect}
          operator={attributes.operator}
          disabled={!attributes.operator}
        />
      )
    }

    if (attributes?.type === 'VOUCHER_USABLE_BY_PROMOTION') {
      return (
        <PromotionSelectInput
          selectedPromotions={selectedPromotionsForVoucher}
          onPromotionSelect={handlePromotionForVoucherSelect}
          operator={attributes.operator}
          disabled={!attributes.operator}
        />
      )
    }

    // Check if this type has predefined values from API (like ORDER_AGENT_TYPE)
    if (selectedType?.values && selectedType.values.length > 0) {
      const isSingleSelect = attributes.operator === 'EQUAL' || attributes.operator === 'NOT_EQUAL'
      
      return (
        <Select
          mode={isSingleSelect ? undefined : "multiple"}
          placeholder={!attributes.operator ? "Please select operator first" : "Select value..."}
          disabled={!attributes.operator}
          value={attributes.value ? (isSingleSelect ? attributes.value : attributes.value.split(',').filter(v => v)) : undefined}
          onChange={(value) => {
            const finalValue = Array.isArray(value) ? value.join(',') : value
            setAttributes({ ...attributes, value: finalValue })
          }}
          style={{ width: '100%' }}
        >
          {selectedType.values.map((item: any) => (
            <Option key={item.code} value={item.code}>
              {item.name}
            </Option>
          ))}
        </Select>
      )
    }
    
    if (TREE_DIMENSIONS.includes(attributes?.type || ''))
      return (
        <TreeSelect
          values={attributes.value ? JSON.parse(attributes?.value) : []}
          onChange={(value) => {
            setAttributes({ ...attributes, value: JSON.stringify(value || '[]') })
          }}
          handleSearch={(keyword) => setKeyword(keyword)}
          options={options}
        />
      )

    // For ORDER_TOTAL_AMOUNT, only allow numbers
    if (attributes?.type === 'ORDER_TOTAL_AMOUNT') {
      return (
        <Input
          className="ant-input"
          placeholder="Enter amount (numbers only)"
          onChange={(v) => {
            const value = v.target.value
            // Only allow numbers and decimal point
            if (/^\d*\.?\d*$/.test(value) || value === '') {
              setAttributes({ ...attributes, value })
            }
          }}
          value={attributes.value}
          name="value"
          min={0}
        />
      )
    }

    return (
      <Input
        className="ant-input"
        placeholder="Value"
        onChange={(v) => {
          setAttributes({ ...attributes, value: v.target.value })
        }}
        value={attributes.value}
        name="value"
        min={0}
      ></Input>
    )
  }

  const renderConfigContent = () => {
    return (
      <div className="CheckUserAttrForm">
        <div className="form-item type">
          <Select
            placeholder="Type"
            onChange={(v) => {
              setAttributes({
                operator: undefined,
                value: undefined,
                timeOperator: undefined,
                time_value: undefined,
                type: v,
              })
              setSelectedProducts([])
              setSelectedCategories([])
              setSelectedPromotions([])
              setSelectedVouchers([])
              setSelectedPromotionsForVoucher([])
            }}
            value={attributes.type}
            // name="type"
          >
            {dimensions.map((item) => (
              <Option key={item.code} value={item.code}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="form-item">
          <Select
            onChange={(v) => {
              const singleSelectOps = ['EQUAL', 'NOT_EQUAL']
              const multiSelectOps = ['IN_THE_FOLLOWING', 'NOT_IN_THE_FOLLOWING']
              
              const isFromSingle = attributes.operator ? singleSelectOps.includes(attributes.operator) : false
              const isToSingle = singleSelectOps.includes(v)
              const isFromMulti = attributes.operator ? multiSelectOps.includes(attributes.operator) : false
              const isToMulti = multiSelectOps.includes(v)
              
              // Clear values when switching between single/multi select modes
              if ((isFromSingle && isToMulti) || (isFromMulti && isToSingle)) {
                setAttributes({ ...attributes, operator: v, value: undefined })
                // Clear selected items
                if (attributes?.type === 'ORDER_HAS_PRODUCT') {
                  setSelectedProducts([])
                }
                if (attributes?.type === 'ORDER_HAS_PRODUCT_IN_CATEGORY') {
                  setSelectedCategories([])
                }
                if (attributes?.type === 'ORDER_HAS_PROMOTION') {
                  setSelectedPromotions([])
                }
                if (attributes?.type === 'ORDER_HAS_VOUCHER') {
                  setSelectedVouchers([])
                }
                if (attributes?.type === 'VOUCHER_USABLE_BY_PROMOTION') {
                  setSelectedPromotionsForVoucher([])
                }
              } else {
                // Just change operator without clearing for compatible changes
                setAttributes({ ...attributes, operator: v })
              }
            }}
            value={attributes.operator}
            placeholder="Operator"
            // name="operator"
          >
            {renderOptions('operator')}
          </Select>
        </div>
        <div className="form-item">{renderOptionsInput()}</div>

        {/* Time operator */}
        <div className="form-item">
          {selectedType?.timeOperator?.length ? (
            <div className="flex">
              <div className="mr-4">
                <Select
                  placeholder="When"
                  value={attributes?.timeOperator}
                  onChange={(v) => {
                    setAttributes({ ...attributes, timeOperator: v })
                  }}
                >
                  {renderOptions('timeOperator')}
                </Select>
              </div>
              <div className="mr-4" style={{ width: 200 }}>
                <TimeInput
                  timeOperator={{
                    name: attributes.timeOperator,
                    values:
                      selectedType?.timeOperator?.find(
                        (item: any) => item.code === attributes.timeOperator
                      )?.values || {},
                  }}
                  value={attributes.time_value || ''}
                  onChange={(v) => {
                    if (v) {
                      setAttributes({ ...attributes, time_value: v })
                    }
                  }}
                  isEditing={true}
                />
              </div>
            </div>
          ) : null}
        </div>
        {selectedType?.extraField?.filter((e: any) => e?.code === 'count')?.length ? (
          <div>
            <span className="mr-2">count</span>
            <Input
              style={{ height: 40, borderRadius: 4, width: 100, marginRight: 8 }}
              placeholder="Value"
              value={attributes.count}
              onChange={(ev) => {
                setAttributes({ ...attributes, count: ev.target.value.toString() })
              }}
            />
          </div>
        ) : null}
      </div>
    )
  }

  const onOk = () => {
    if (editingSegment) {
      // Update existing segment
      updateSegment({
        groupIndex: currentGroup,
        index: editingSegment.index,
        segment: attributes,
      })
    } else {
      // Add new segment
      addSegment({
        audience: attributes,
      })
    }
  }

  const onDelete = () => {
    if (editingSegment) {
      delSegment({
        groupIndex: currentGroup,
        index: editingSegment.index,
      })
      onDimensionModalCancel()
    }
  }

  return (
    <Modal
      title={editingSegment ? "Edit user attribute" : "Add user attribute"}
      className="CheckUserAttrModal"
      closable
      width={1200}
      onOk={() => onOk()}
      onCancel={() => onDimensionModalCancel()}
      visible={visible}
      footer={[
        editingSegment && (
          <Button
            key="delete"
            danger
            onClick={onDelete}
            style={{ float: 'left' }}
          >
            Delete
          </Button>
        ),
        <Button key="cancel" onClick={onDimensionModalCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={onOk}>
          {editingSegment ? 'Update' : 'Save'}
        </Button>,
      ]}
    >
      {renderConfigContent()}
    </Modal>
  )
}

export default connect(
  (state: any) => ({
    dimensions: state.segmentation.dimensions,
    editingSegment: state.segmentation.editingSegment,
    currentGroup: state.segmentation.currentGroup,
  }),
  (dispatch: any) => ({
    addSegment: dispatch.segmentation.addSegment,
    updateSegment: dispatch.segmentation.updateSegment,
    delSegment: dispatch.segmentation.delSegment,
    toggleDimensionModal: dispatch.segmentation.toggleDimensionModal,
    onDimensionModalCancel: dispatch.segmentation.onDimensionModalCancel,
  })
)(UserAttributeModal2)
